// src/components/Admin/Cantieri/CommittentiManagement.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy
} from 'firebase/firestore';
import { db } from '../../../firebase';
import './CommittentiManagement.css';


const CommittentiManagement = ({ 
  onCreateCantiere, 
  onViewCantiere,
  selectedCommittenteId = null,
  showCommittenteDetail = false,
  viewMode = null
}) => {
  const [committenti, setCommittenti] = useState([]);
  const [filteredCommittenti, setFilteredCommittenti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCommittente, setEditingCommittente] = useState(null);
  const [selectedCommittente, setSelectedCommittente] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Filtri - AGGIORNATO con showHidden
  const [filters, setFilters] = useState({
    searchTerm: '',
    hasAddress: 'all',
    hasEmail: 'all',
    showHidden: false // NUOVO: mostra/nascondi committenti nascosti
  });

  // Form state
  const [formData, setFormData] = useState({
    ragioneSociale: '',
    codiceFiscale: '',
    partitaIva: '',
    indirizzo: '',
    telefono: '',
    email: '',
    note: ''
  });

  // ========================================
  // FUNZIONI FIREBASE
  // ========================================

  const getCommittenti = async () => {
    try {
      const committentiRef = collection(db, 'committenti');
      const q = query(committentiRef, orderBy('ragioneSociale', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
      });
    } catch (error) {
      console.error('Errore nel recupero committenti:', error);
      return [];
    }
  };

  const getCantieri = async () => {
    try {
      const cantieriRef = collection(db, 'cantieri');
      const querySnapshot = await getDocs(cantieriRef);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dataInizioLavori: data.dataInizioLavori?.toDate?.() || data.dataInizioLavori,
          dataFineLavori: data.dataFineLavori?.toDate?.() || data.dataFineLavori,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
      });
    } catch (error) {
      console.error('Errore nel recupero cantieri:', error);
      return [];
    }
  };

  const createCommittente = async (committenteData) => {
    try {
      const committenteToSave = {
        ...committenteData,
        isHidden: false, // NUOVO: inizializza come non nascosto
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const committentiRef = collection(db, "committenti");
      const docRef = await addDoc(committentiRef, committenteToSave);
      return docRef.id;
    } catch (error) {
      console.error("Errore nella creazione committente:", error);
      throw error;
    }
  };

  const updateCommittente = async (committenteId, committenteData) => {
    try {
      const updateData = {
        ...committenteData,
        updatedAt: new Date()
      };

      const committenteRef = doc(db, "committenti", committenteId);
      await updateDoc(committenteRef, updateData);
      return true;
    } catch (error) {
      console.error("Errore nell'aggiornamento committente:", error);
      throw error;
    }
  };

  const deleteCommittente = async (committenteId) => {
    try {
      const committenteRef = doc(db, "committenti", committenteId);
      await deleteDoc(committenteRef);
      return true;
    } catch (error) {
      console.error("Errore nell'eliminazione committente:", error);
      throw error;
    }
  };

  // NUOVA FUNZIONE: Nascondi/Mostra committente
  const toggleHideCommittente = async (committenteId, isHidden) => {
    try {
      const committenteRef = doc(db, "committenti", committenteId);
      await updateDoc(committenteRef, {
        isHidden: isHidden,
        updatedAt: new Date()
      });
      return true;
    } catch (error) {
      console.error("Errore nel cambiare visibilità committente:", error);
      throw error;
    }
  };

  // ========================================
  // EFFECTS
  // ========================================

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [committenti, filters]);

  useEffect(() => {
    if (viewMode === 'detail' && selectedCommittenteId) {
      loadSpecificCommittente(selectedCommittenteId);
    } else if (!viewMode) {
      setSelectedCommittente(null);
    }
  }, [viewMode, selectedCommittenteId]);

  // ========================================
  // FUNZIONI CARICAMENTO DATI
  // ========================================

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      const [committentiData, cantieriData] = await Promise.all([
        getCommittenti(),
        getCantieri()
      ]);
      
      setCommittenti(committentiData);
      setCantieri(cantieriData);
      
      console.log(`Caricati: ${committentiData.length} committenti, ${cantieriData.length} cantieri`);
    } catch (error) {
      console.error('Errore nel caricamento dati:', error);
      showNotification('Errore nel caricamento dei dati', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSpecificCommittente = async (committenteId) => {
    try {
      setIsLoading(true);
      console.log('Caricamento committente specifico:', committenteId);
      
      if (committenti.length === 0) {
        await loadInitialData();
      }
      
      const committente = committenti.find(c => c.id === committenteId);
      if (committente) {
        setSelectedCommittente(committente);
        console.log('Committente caricato per dettaglio:', committente);
      } else {
        console.error('Committente non trovato:', committenteId);
        showNotification('Committente non trovato', 'error');
      }
    } catch (error) {
      console.error('Errore nel caricamento committente:', error);
      showNotification('Errore nel caricamento del committente', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // FUNZIONI FILTRI
  // ========================================

  const applyFilters = () => {
    let filtered = [...committenti];

    // NUOVO: Filtra committenti nascosti se non richiesti
    if (!filters.showHidden) {
      filtered = filtered.filter(committente => !committente.isHidden);
    }

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(committente =>
        committente.ragioneSociale.toLowerCase().includes(searchLower) ||
        (committente.codiceFiscale && committente.codiceFiscale.toLowerCase().includes(searchLower)) ||
        (committente.partitaIva && committente.partitaIva.toLowerCase().includes(searchLower)) ||
        (committente.email && committente.email.toLowerCase().includes(searchLower))
      );
    }

    if (filters.hasAddress !== 'all') {
      const hasAddress = filters.hasAddress === 'true';
      filtered = filtered.filter(committente => 
        hasAddress ? (committente.indirizzo && committente.indirizzo.trim()) : 
                    (!committente.indirizzo || !committente.indirizzo.trim())
      );
    }

    if (filters.hasEmail !== 'all') {
      const hasEmail = filters.hasEmail === 'true';
      filtered = filtered.filter(committente => 
        hasEmail ? (committente.email && committente.email.trim()) : 
                  (!committente.email || !committente.email.trim())
      );
    }

    setFilteredCommittenti(filtered);
  };

  // ========================================
  // FUNZIONI UI
  // ========================================

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.ragioneSociale.trim()) {
      showNotification('La ragione sociale è obbligatoria', 'error');
      return;
    }

    try {
      if (editingCommittente) {
        await updateCommittente(editingCommittente.id, formData);
        showNotification('Committente aggiornato con successo', 'success');
      } else {
        await createCommittente(formData);
        showNotification('Committente creato con successo', 'success');
      }
      
      resetForm();
      loadInitialData();
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      showNotification(error.message || 'Errore nel salvataggio del committente', 'error');
    }
  };

  const handleEdit = (committente) => {
    setEditingCommittente(committente);
    setFormData({
      ragioneSociale: committente.ragioneSociale || '',
      codiceFiscale: committente.codiceFiscale || '',
      partitaIva: committente.partitaIva || '',
      indirizzo: committente.indirizzo || '',
      telefono: committente.telefono || '',
      email: committente.email || '',
      note: committente.note || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (committente) => {
    const cantieriAssociati = cantieri.filter(c => c.committenteId === committente.id);
    
    if (cantieriAssociati.length > 0) {
      showNotification(
        `Impossibile eliminare: ci sono ${cantieriAssociati.length} cantieri associati a questo committente`, 
        'error'
      );
      return;
    }

    if (window.confirm(`Sei sicuro di voler eliminare il committente "${committente.ragioneSociale}"?\nQuesta azione non può essere annullata.`)) {
      try {
        await deleteCommittente(committente.id);
        showNotification('Committente eliminato con successo', 'success');
        loadInitialData();
      } catch (error) {
        console.error('Errore nell\'eliminazione:', error);
        showNotification(error.message || 'Errore nell\'eliminazione del committente', 'error');
      }
    }
  };

  // NUOVA FUNZIONE: Gestisce nascondere/mostrare committente
  const handleToggleHide = async (committente) => {
    const action = committente.isHidden ? 'mostrare' : 'nascondere';
    
    if (window.confirm(`Sei sicuro di voler ${action} il committente "${committente.ragioneSociale}"?`)) {
      try {
        await toggleHideCommittente(committente.id, !committente.isHidden);
        showNotification(`Committente ${committente.isHidden ? 'mostrato' : 'nascosto'} con successo`, 'success');
        loadInitialData();
      } catch (error) {
        console.error(`Errore nel ${action}:`, error);
        showNotification(`Errore nel ${action} il committente`, 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      ragioneSociale: '',
      codiceFiscale: '',
      partitaIva: '',
      indirizzo: '',
      telefono: '',
      email: '',
      note: ''
    });
    setEditingCommittente(null);
    setShowForm(false);
  };

  const handleViewDetails = (committente) => {
    setSelectedCommittente(committente);
  };

  const handleViewCantiere = (cantiere) => {
    if (onViewCantiere) {
      onViewCantiere(cantiere.id);
    } else {
      console.log('Apertura cantiere:', cantiere);
    }
  };

  const getCantieriForCommittente = (committenteId) => {
    return cantieri.filter(cantiere => cantiere.committenteId === committenteId);
  };

  // ========================================
  // RENDERING
  // ========================================

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Caricamento committenti...</p>
      </div>
    );
  }

  // Vista dettaglio committente
  if (selectedCommittente || (viewMode === 'detail' && selectedCommittenteId)) {
    const committente = selectedCommittente || committenti.find(c => c.id === selectedCommittenteId);
    if (committente) {
      const cantieriCommittente = getCantieriForCommittente(committente.id);
      return (
        <CommittenteDetail 
          committente={committente}
          cantieri={cantieriCommittente}
          onBack={() => {
            setSelectedCommittente(null);
          }}
          onEdit={() => {
            handleEdit(committente);
            if (viewMode !== 'detail') {
              setSelectedCommittente(null);
            }
          }}
          onCreateCantiere={onCreateCantiere}
          onViewCantiere={handleViewCantiere}
        />
      );
    }
  }

  // Vista principale con griglia committenti
  return (
    <div className="committenti-management">
      {/* Notifiche */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="section-header">
        <h2>🏢 Gestione Committenti</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          ➕ Nuovo Committente
        </button>
      </div>

      {/* Filtri - AGGIORNATI */}
      <div className="filters-container">
        <div className="filters-grid">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Cerca per ragione sociale, CF, P.IVA o email..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={filters.hasAddress}
              onChange={(e) => setFilters({...filters, hasAddress: e.target.value})}
              className="filter-select"
            >
              <option value="all">Tutti gli indirizzi</option>
              <option value="true">Con indirizzo</option>
              <option value="false">Senza indirizzo</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filters.hasEmail}
              onChange={(e) => setFilters({...filters, hasEmail: e.target.value})}
              className="filter-select"
            >
              <option value="all">Tutte le email</option>
              <option value="true">Con email</option>
              <option value="false">Senza email</option>
            </select>
          </div>

          {/* NUOVO: Filtro per mostrare committenti nascosti */}
          <div className="filter-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.showHidden}
                onChange={(e) => setFilters({...filters, showHidden: e.target.checked})}
              />
              <span>Mostra committenti nascosti</span>
            </label>
          </div>
        </div>
      </div>

      {/* Griglia Committenti - AGGIORNATA */}
      {filteredCommittenti.length > 0 ? (
        <div className="committenti-grid">
          {filteredCommittenti.map(committente => {
            const cantieriCount = getCantieriForCommittente(committente.id).length;
            const isHidden = committente.isHidden || false;
            
            return (
              <div key={committente.id} className={`committente-card ${isHidden ? 'hidden-committente' : ''}`}>
                <div className="card-header">
                  <div className="card-title">
                    <h3>
                      {committente.ragioneSociale}
                      {isHidden && <span className="hidden-badge">👻 Nascosto</span>}
                    </h3>
                    <div className="cantieri-badge">
                      {cantieriCount} cantier{cantieriCount !== 1 ? 'i' : 'e'}
                    </div>
                  </div>
<div className="card-actions">
  {/* Bottone Visualizza */}
  <button
    className="btn-action btn-view"
    onClick={() => handleViewDetails(committente)}
    title="Visualizza dettagli"
  >
    <svg 
      width="18" 
      height="18" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  </button>

  {/* Bottone Modifica */}
  <button
    className="btn-action btn-edit"
    onClick={() => handleEdit(committente)}
    title="Modifica"
  >
    <svg 
      width="18" 
      height="18" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
  </button>

  {/* Bottone Nascondi/Mostra */}
  <button
    className={`btn-action ${isHidden ? 'btn-show' : 'btn-hide'}`}
    onClick={() => handleToggleHide(committente)}
    title={isHidden ? 'Mostra committente' : 'Nascondi committente'}
  >
    {isHidden ? (
      // Icona Mostra (Occhio)
      <svg 
        width="18" 
        height="18" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
      </svg>
    ) : (
      // Icona Nascondi (Occhio barrato)
      <svg 
        width="18" 
        height="18" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
        <line x1="1" y1="1" x2="23" y2="23"></line>
      </svg>
    )}
  </button>

  {/* Bottone Elimina */}
  <button
    className="btn-action btn-delete"
    onClick={() => handleDelete(committente)}
    title="Elimina"
  >
    <svg 
      width="18" 
      height="18" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
  </button>
</div>
                </div>
                
                <div className="card-content">
                  {committente.codiceFiscale && (
                    <div className="info-row">
                      <strong>CF:</strong> {committente.codiceFiscale}
                    </div>
                  )}
                  {committente.partitaIva && (
                    <div className="info-row">
                      <strong>P.IVA:</strong> {committente.partitaIva}
                    </div>
                  )}
                  {committente.indirizzo && (
                    <div className="info-row">
                      <strong>Indirizzo:</strong> {committente.indirizzo}
                    </div>
                  )}
                  {committente.telefono && (
                    <div className="info-row">
                      <strong>Tel:</strong> {committente.telefono}
                    </div>
                  )}
                  {committente.email && (
                    <div className="info-row">
                      <strong>Email:</strong> {committente.email}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <h3>Nessun committente trovato</h3>
          <p>
            {committenti.length === 0 ? 
              'Inizia creando il primo committente' : 
              filters.showHidden ? 
                'Non ci sono committenti nascosti' :
                'Prova a modificare i filtri di ricerca'
            }
          </p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {editingCommittente ? 'Modifica Committente' : 'Nuovo Committente'}
              </h3>
              <button className="modal-close" onClick={resetForm}>✖️</button>
            </div>
            
            <form onSubmit={handleFormSubmit} className="committente-form">
              <div className="form-group">
                <label>Ragione Sociale *</label>
                <input
                  type="text"
                  value={formData.ragioneSociale}
                  onChange={(e) => setFormData({...formData, ragioneSociale: e.target.value})}
                  required
                  placeholder="Nome dell'azienda o ente"
                />
              </div>

              <div className="form-group">
                <label>Codice Fiscale</label>
                <input
                  type="text"
                  value={formData.codiceFiscale}
                  onChange={(e) => setFormData({...formData, codiceFiscale: e.target.value})}
                  placeholder="Codice fiscale"
                />
              </div>

              <div className="form-group">
                <label>Partita IVA</label>
                <input
                  type="text"
                  value={formData.partitaIva}
                  onChange={(e) => setFormData({...formData, partitaIva: e.target.value})}
                  placeholder="Partita IVA"
                />
              </div>

              <div className="form-group">
                <label>Indirizzo</label>
                <input
                  type="text"
                  value={formData.indirizzo}
                  onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                  placeholder="Indirizzo completo"
                />
              </div>

              <div className="form-group">
                <label>Telefono</label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  placeholder="Numero di telefono"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Indirizzo email"
                />
              </div>

              <div className="form-group">
                <label>Note</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="Note aggiuntive..."
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCommittente ? 'Aggiorna' : 'Crea'} Committente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// COMPONENTE DETTAGLIO COMMITTENTE
// ========================================

const CommittenteDetail = ({ committente, cantieri, onBack, onEdit, onCreateCantiere, onViewCantiere }) => {
  const formatDate = (date) => {
    if (!date) return 'Non specificata';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const handleCantiereClick = (cantiere) => {
    if (onViewCantiere) {
      onViewCantiere(cantiere);
    }
  };

  const cantieriAttivi = cantieri.filter(c => c.stato === 'Attivo');
  const cantieriArchiviati = cantieri.filter(c => c.stato === 'Archiviato');

  return (
    <div className="committente-detail">
      <div className="detail-header">
        <button className="btn-back" onClick={onBack}>
          ← Torna ai Committenti
        </button>
        <h2>
          🏢 {committente.ragioneSociale}
          {committente.isHidden && <span className="hidden-badge">👻 Nascosto</span>}
        </h2>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={onEdit}
          >
            ✏️ Modifica Committente
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => onCreateCantiere && onCreateCantiere(committente.id)}
          >
            🏗️ Nuovo Cantiere
          </button>
        </div>
      </div>

      <div className="committente-info">
        <div className="info-card">
          <h3>Informazioni Committente</h3>
          <div className="info-grid">
            <div className="info-item">
              <strong>Ragione Sociale:</strong> {committente.ragioneSociale}
            </div>
            {committente.codiceFiscale && (
              <div className="info-item">
                <strong>Codice Fiscale:</strong> {committente.codiceFiscale}
              </div>
            )}
            {committente.partitaIva && (
              <div className="info-item">
                <strong>Partita IVA:</strong> {committente.partitaIva}
              </div>
            )}
            {committente.indirizzo && (
              <div className="info-item">
                <strong>Indirizzo:</strong> {committente.indirizzo}
              </div>
            )}
            {committente.telefono && (
              <div className="info-item">
                <strong>Telefono:</strong> {committente.telefono}
              </div>
            )}
            {committente.email && (
              <div className="info-item">
                <strong>Email:</strong> {committente.email}
              </div>
            )}
            {committente.note && (
              <div className="info-item full-width">
                <strong>Note:</strong> {committente.note}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="cantieri-sections">
        {cantieriAttivi.length > 0 && (
          <div className="cantieri-section">
            <h3>🏗️ Cantieri Attivi ({cantieriAttivi.length})</h3>
            <p className="section-hint">💡 Clicca sui nomi dei cantieri per aprirli e lavorarci</p>
            <div className="cantieri-grid">
              {cantieriAttivi.map(cantiere => (
                <div key={cantiere.id} className="cantiere-card">
                  <div className="cantiere-header">
                    <h4 
                      className="cantiere-title clickable"
                      onClick={() => handleCantiereClick(cantiere)}
                      title="Clicca per aprire il cantiere"
                    >
                      {cantiere.nomeCommessa}
                    </h4>
                    <span className="status-badge active">Attivo</span>
                  </div>
                  <div className="cantiere-info">
                    {cantiere.luogo && <p><strong>Luogo:</strong> {cantiere.luogo}</p>}
                    {cantiere.contratto && <p><strong>Contratto:</strong> {cantiere.contratto}</p>}
                    <p><strong>Inizio:</strong> {formatDate(cantiere.dataInizioLavori)}</p>
                    {cantiere.dataFineLavori && (
                      <p><strong>Fine:</strong> {formatDate(cantiere.dataFineLavori)}</p>
                    )}
                  </div>
                  <div className="cantiere-actions">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => handleCantiereClick(cantiere)}
                      title="Apri cantiere"
                    >
                      🚀 Apri Cantiere
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {cantieriArchiviati.length > 0 && (
          <div className="cantieri-section">
            <h3>📁 Cantieri Archiviati ({cantieriArchiviati.length})</h3>
            <p className="section-hint">💡 Clicca sui nomi dei cantieri per visualizzarli</p>
            <div className="cantieri-grid">
              {cantieriArchiviati.map(cantiere => (
                <div key={cantiere.id} className="cantiere-card archived">
                  <div className="cantiere-header">
                    <h4 
                      className="cantiere-title clickable archived"
                      onClick={() => handleCantiereClick(cantiere)}
                      title="Clicca per visualizzare il cantiere"
                    >
                      {cantiere.nomeCommessa}
                    </h4>
                    <span className="status-badge archived">Archiviato</span>
                  </div>
                  <div className="cantiere-info">
                    {cantiere.luogo && <p><strong>Luogo:</strong> {cantiere.luogo}</p>}
                    {cantiere.contratto && <p><strong>Contratto:</strong> {cantiere.contratto}</p>}
                    <p><strong>Inizio:</strong> {formatDate(cantiere.dataInizioLavori)}</p>
                    {cantiere.dataFineLavori && (
                      <p><strong>Fine:</strong> {formatDate(cantiere.dataFineLavori)}</p>
                    )}
                  </div>
                  <div className="cantiere-actions">
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleCantiereClick(cantiere)}
                      title="Visualizza cantiere"
                    >
                      👁️ Visualizza
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {cantieri.length === 0 && (
          <div className="no-cantieri">
            <h3>📋 Nessun cantiere associato</h3>
            <p>Questo committente non ha ancora cantieri associati.</p>
            <button 
              className="btn btn-primary"
              onClick={() => onCreateCantiere && onCreateCantiere(committente.id)}
            >
              🏗️ Crea il primo cantiere
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommittentiManagement;